/* eslint-disable react/require-default-props, no-nested-ternary */
import moment from 'moment';
import React from 'react';
import isString from 'lodash/isString';
import { useTranslation } from 'react-i18next';
import { momentLocalizer, NavigateAction, View } from 'react-big-calendar';
import { CalendarEventType, OnSelectCalendarEventType } from '../CalendarEventType';
import { CalendarAvailabilityType } from '../CalendarAvailabilityType';
import { CalendarScheduleTypeEnum } from '../CalendarScheduleTypeEnum';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from '../CalendarSidebarTab';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import {
  ActiveAppointmentAvailabilitySlot,
  AppointmentAvailability,
} from '../../../firebase/firestore/documents/appointmentAvaiability';
import { Recruiter } from '../../../firebase/firestore/documents/recruiter';
import { dayFormat, dayRangeHeaderFormat, getDateRangeForView } from './utils/calendarUtils';
import { useCalendarEventStyles } from './hooks/useCalendarEventStyles';
import '../Dualtimezone/Dualtimezonegutter.css';
import { useCalendarTimezone } from './hooks/useCalendarTimezone';
import { useCalendarEvents } from './hooks/useCalendarEvents';
import { CalendarDrawers } from './CalendarDrawers';
import { CalendarBase } from './CalendarBase';
import { useCalendarComponents } from './hooks/useCalendarComponents';
import { useTimezone } from '../hooks/UseTimezone';

interface AvailabilityCalendarProps {
  checkedPositions?: string[];
  mergedRecruiters: Recruiter[];
  administrationCalendar: boolean;
  setActiveDateRange: (fromDate: Date, toDate: Date) => void;
  setSelectedSchedule: (schedule: AppointmentAvailability | undefined) => void;
  setActiveAppointmentSlot: (availability: ActiveAppointmentAvailabilitySlot | undefined) => void;
  setIsAppointmentDrawerOpen: (isOpen: boolean) => void;
  activeDate: Date;
  setActiveDate: (date: Date) => void;
  activeAvailability: AppointmentAvailability | undefined;
  setActiveAvailability: (availability: AppointmentAvailability | undefined) => void;
  activeTab?: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum;
}

function AvailabilityCalendar({
  checkedPositions,
  mergedRecruiters,
  administrationCalendar,
  activeTab,
  setSelectedSchedule,
  setActiveAppointmentSlot,
  activeDate,
  setActiveDate,
  activeAvailability,
  setActiveAvailability,
  setIsAppointmentDrawerOpen,
  setActiveDateRange,
}: AvailabilityCalendarProps) {
  const { i18n } = useTranslation('calendar');

  const accountStore = useStoreState((s) => s.app.accounts);
  const { calendarPresenter, groupEventPresenter: calendarGroupEventsPresenter } = useStoreState((s) => s.calendar);
  const { googleCalendarEvent, microsoftCalendarEvent, currentQuarterOnCalendar } = useStoreState((state) => state.calendar);
  const { setCurrentQuarterOnCalendar } = useStoreActions((action) => action.calendar);

  const { isCalendarTzEnabled, selectedTz, primaryTz, secondaryTz, now } = useTimezone();

  const localizer = React.useMemo(() => momentLocalizer(moment), []);
  moment.updateLocale('en', { week: { dow: 0 } });

  const [calendarCurrentView, setCalendarCurrentView] = React.useState<string>('week-view');
  const [groupEventEnabled, setGroupEvenEnabled] = React.useState<boolean | undefined>(false);
  const [isAvailabilityDetailDrawerOpen, setIsAvailabilityDetailDrawerOpen] = React.useState<boolean>(false);
  const [selectedEventForDetail, setSelectedEventForDetail] = React.useState<CalendarEventType | undefined>(undefined);
  const [isGoogleEventDetailDrawerOpen, setIsGoogleEventDetailDrawerOpen] = React.useState<boolean>(false);

  /* Group event toggle */
  React.useEffect(() => {
    // istanbul ignore next
    if (accountStore) {
      setGroupEvenEnabled(accountStore?.configuration?.groupEventEnabled);
    } else {
      setGroupEvenEnabled(false);
    }
  }, [accountStore]);

  const formats = React.useMemo(
    () => ({
      dayFormat,
      dayRangeHeaderFormat,
      timeGutterFormat: /* istanbul ignore next */ isCalendarTzEnabled
        ? () => ''
        : /* eslint-disable-next-line */
          (date: Date, culture: any, loc: any) => loc.format(date, 'h:mm A', culture),
    }),
    [isCalendarTzEnabled],
  );

  const { handleEventType } = useCalendarEventStyles({ administrationCalendar, mergedRecruiters, activeTab });

  const handleEventOfDate = React.useCallback(
    (event: CalendarEventType) => {
      let date: Date;
      // istanbul ignore next
      if (isString(event.start)) date = moment(event.start).toDate();
      else date = event.start;

      // istanbul ignore next
      if (
        activeTab === RecruiterCalendarSidebarTabTypeEnum.SCHEDULE ||
        activeTab === RecruiterCalendarSidebarTabTypeEnum.MY_POSITION ||
        activeTab === CalendarSidebarTabTypeEnum.RECRUITERS ||
        activeTab === CalendarSidebarTabTypeEnum.FOLLOWED_POSITIONS
      ) {
        setActiveDate(date);
        // istanbul ignore next
        if (event.resource.type === CalendarScheduleTypeEnum.AVAILABILITY) {
          const { availability } = event.resource as CalendarAvailabilityType;
          setActiveAvailability(availability);
          setIsAvailabilityDetailDrawerOpen(true);
        }
      }

      // istanbul ignore next
      if (
        !administrationCalendar &&
        (event.resource.type === CalendarScheduleTypeEnum.GOOGLE_EVENT ||
          event.resource.type === CalendarScheduleTypeEnum.OUTLOOK_EVENT)
      ) {
        setSelectedEventForDetail(event);
        setIsGoogleEventDetailDrawerOpen(true);
      }
    },
    [activeTab, administrationCalendar, setActiveDate, setActiveAvailability],
  );

  const handleSelect = React.useCallback(
    ({ start, end }: OnSelectCalendarEventType) => {
      // Helper to strip browser TZ and apply the selected TZ
      const applyWallTimeToTimezone = (browserDate: Date, targetTz: string) => {
        // Format strips the browser's local timezone offset
        const wallTimeStr = moment(browserDate).format('YYYY-MM-DDTHH:mm:ss');
        // Parse the literal time as if it happened in the target timezone
        return moment.tz(wallTimeStr, targetTz).toDate();
      };

      // Determine the actual start and end dates based on whether the TZ feature is active
      const actualStart =
        isCalendarTzEnabled && selectedTz
          ? applyWallTimeToTimezone(start as Date, selectedTz)
          : /* istanbul ignore next */ isString(start)
          ? moment(start).toDate()
          : start;

      const actualEnd =
        isCalendarTzEnabled && selectedTz
          ? applyWallTimeToTimezone(end as Date, selectedTz)
          : /* istanbul ignore next */ isString(end)
          ? moment(end).toDate()
          : end;

      setActiveAvailability(undefined);
      setActiveDate(actualStart);

      // Use moment to check the difference to ensure timezone-safe comparison
      // istanbul ignore next
      if (moment(actualStart).diff(moment(now()), 'minutes') > 1) {
        setIsAppointmentDrawerOpen(true);
        setActiveAppointmentSlot({ fromDate: actualStart, toDate: actualEnd });
      }
    },
    [
      isCalendarTzEnabled,
      selectedTz,
      now,
      setActiveAvailability,
      setActiveDate,
      setIsAppointmentDrawerOpen,
      setActiveAppointmentSlot,
    ],
  );

  const setCurrentDateRangeForViews = React.useCallback(
    (givenDate: Date, view: View): void => {
      const tz = isCalendarTzEnabled ? selectedTz : undefined;
      const { fromDate, toDate } = getDateRangeForView(givenDate, view, tz);
      setActiveDateRange(fromDate, toDate);
    },
    [isCalendarTzEnabled, selectedTz, setActiveDateRange],
  );

  const handleNavigation = React.useCallback(
    (newDate: Date, view: View, action: NavigateAction) => {
      setCurrentDateRangeForViews(newDate, view);
      if (moment(newDate).quarter() !== currentQuarterOnCalendar.quarter) {
        setCurrentQuarterOnCalendar({ quarter: moment(newDate).quarter(), date: newDate });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      if (action === 'TODAY') {
        setActiveDate(newDate);
      }
    },
    [setCurrentDateRangeForViews, currentQuarterOnCalendar.quarter, setCurrentQuarterOnCalendar, setActiveDate],
  );

  React.useEffect(() => {
    handleNavigation(activeDate, 'week', 'TODAY');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // istanbul ignore next
  const handleCalendarView = React.useCallback((view: View) => {
    setCalendarCurrentView(`${view}-view`);
  }, []);

  // istanbul ignore next
  const openAppointmentDrawerUpdateBooking = React.useCallback(
    (availability: AppointmentAvailability) => {
      setSelectedSchedule(availability);
      setIsAppointmentDrawerOpen(true);
    },
    [setSelectedSchedule, setIsAppointmentDrawerOpen],
  );

  // istanbul ignore next
  const handleCloseAvailabilityDetail = React.useCallback(() => setIsAvailabilityDetailDrawerOpen(false), []);
  // istanbul ignore next
  const handleCloseGoogleEventDetail = React.useCallback(() => setIsGoogleEventDetailDrawerOpen(false), []);

  const calendarMessages = React.useMemo(() => {
    // istanbul ignore next
    if (i18n.language === 'fr') {
      return {
        today: `Aujourd'hui`,
        month: 'Mois',
        week: 'Semaine',
        day: 'Jour',
        showMore: (total: number) => `+${total} de plus`,
      };
    }
    return { today: 'Today', month: 'Month', week: 'Week', day: 'Day' };
  }, [i18n.language]);

  const { events: normalizedEvents, hasAnyAllDayEvent } = useCalendarEvents({
    administrationCalendar,
    activeTab,
    checkedPositions,
    calendarCurrentView,
    groupEventEnabled,
    activeDate,
    calendarPresenter,
    calendarGroupEventsPresenter,
    googleCalendarEvent,
    microsoftCalendarEvent,
  });

  const { transformedEvents, calendarNow, scrollToTime } = useCalendarTimezone({
    events: normalizedEvents,
    showDualGutter: isCalendarTzEnabled,
    primaryTimezone: selectedTz,
  });

  const activeRecruiterInfo = React.useMemo(
    () => mergedRecruiters.find((rec) => rec.id === activeAvailability?.userId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedRecruiters, activeAvailability?.userId],
  );

  const calendarComponents = useCalendarComponents({
    showDualGutter: isCalendarTzEnabled,
    primaryTimezone: primaryTz,
    secondaryTimezone: secondaryTz,
    activeTab,
    checkedPositions,
    administrationCalendar,
    openAppointmentDrawerUpdateBooking,
  });

  return (
    <>
      <CalendarDrawers
        isAvailabilityDetailDrawerOpen={isAvailabilityDetailDrawerOpen}
        activeAvailability={activeAvailability}
        calendarPresenter={calendarPresenter}
        activeRecruiterInfo={activeRecruiterInfo}
        handleCloseAvailabilityDetail={handleCloseAvailabilityDetail}
        isGoogleEventDetailDrawerOpen={isGoogleEventDetailDrawerOpen}
        selectedEventForDetail={selectedEventForDetail}
        handleCloseGoogleEventDetail={handleCloseGoogleEventDetail}
      />

      <CalendarBase
        formats={formats}
        localizer={localizer}
        events={transformedEvents}
        scrollToTime={scrollToTime}
        calendarCurrentView={calendarCurrentView}
        hasAnyAllDayEvent={hasAnyAllDayEvent}
        handleEventOfDate={handleEventOfDate}
        handleSelect={handleSelect}
        handleEventType={handleEventType}
        handleNavigation={handleNavigation}
        handleCalendarView={handleCalendarView}
        calendarComponents={calendarComponents}
        calendarMessages={calendarMessages}
        calendarNow={calendarNow}
        showDualGutter={isCalendarTzEnabled}
      />
    </>
  );
}

export default React.memo(AvailabilityCalendar);
